import React from 'react';
import { Link } from 'react-router-dom';
import { Music } from 'lucide-react';
import { useCookieConsent } from './CookieConsent';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { showCookieSettings } = useCookieConsent();
  
  return (
    <footer className="bg-black/90 border-t border-white/10 mt-12">
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8 mb-8">
          {/* Logo and brief description */}
          <div className="md:col-span-1">
            <Link to="/">
              <img 
                src="/logo/DK Monogram Outline WH.svg" 
                alt="Daryian Kelton Logo" 
                className="h-16 mb-4"
                width="64"
                height="64"
              />
            </Link>
            <p className="text-white mt-2">
              Drums & Visuals. Musician, photographer, and videographer stretching the limits of creativity.
            </p>
            <div className="flex space-x-4 mt-4">
              {/* Social media links */}
              <a href="https://instagram.com/drummerboy_x" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#22FFAA] transition-colors" aria-label="Follow Daryian Kelton on Instagram">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                <span className="sr-only">Instagram</span>
              </a>
              <a href="https://facebook.com/drummerboy-xd" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#22FFAA] transition-colors" aria-label="Follow Daryian Kelton on Facebook">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                <span className="sr-only">Facebook</span>
              </a>
              <a href="https://youtube.com/@DaryianKelton" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#22FFAA] transition-colors" aria-label="Subscribe to Daryian Kelton on YouTube">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
                <span className="sr-only">YouTube</span>
              </a>
            </div>
          </div>
          
          {/* Quick links */}
          <div>
            <h3 className="text-white text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-white hover:text-[#22FFAA] transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-white hover:text-[#22FFAA] transition-colors">About</Link></li>
              <li><Link to="/videos" className="text-white hover:text-[#22FFAA] transition-colors">Videos</Link></li>
              <li><Link to="/services" className="text-white hover:text-[#22FFAA] transition-colors">Services</Link></li>
              <li><Link to="/contact" className="text-white hover:text-[#22FFAA] transition-colors">Contact</Link></li>
              <li>
                <a 
                  href="https://seriousmattersnj.com" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="serious-matters-link"
                >
                  Serious Matters
                </a>
              </li>
            </ul>
          </div>
          
          {/* Legal */}
          <div>
            <h3 className="text-white text-lg font-bold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li><Link to="/privacy" className="text-white hover:text-[#22FFAA] transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-white hover:text-[#22FFAA] transition-colors">Terms of Use</Link></li>
              <li><button onClick={showCookieSettings} className="text-white hover:text-[#22FFAA] transition-colors">Cookie Settings</button></li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h3 className="text-white text-lg font-bold mb-4">Contact</h3>
            <ul className="space-y-2">
              <li><a href="mailto:contact@daryiankelton.com" className="text-white hover:text-[#22FFAA] transition-colors">contact@daryiankelton.com</a></li>
              <li>
                <Link 
                  to="/testimonials" 
                  className="text-client-yellow hover:text-client-teal transition-colors font-medium"
                >
                  Share Feedback
                </Link>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-white text-sm">
              © {currentYear} Daryian Kelton. All rights reserved.
            </p>
            <div className="flex items-center mt-4 md:mt-0">
              <span className="text-white text-sm">Made with <Music className="inline h-4 w-4 text-white mx-1" /> by <a href="https://jukeboxjay.com" target="_blank" rel="noopener noreferrer" className="text-[#22FFAA] hover:text-client-yellow transition-colors">Jukeboxjay</a></span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
